import React, { useState, useCallback } from 'react';
import { useAuthStore } from '../../stores/authStore';

interface RegisterProps {
  onSwitchToLogin: () => void;
  onSuccess: () => void;
}

type PasswordStrength = 'weak' | 'medium' | 'strong';

function evaluatePasswordStrength(password: string): PasswordStrength {
  if (password.length < 8) return 'weak';
  let score = 0;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 2) return 'weak';
  if (score === 3) return 'medium';
  return 'strong';
}

function strengthLabel(strength: PasswordStrength): string {
  if (strength === 'weak') return 'Weak';
  if (strength === 'medium') return 'Medium';
  return 'Strong';
}

function strengthColor(strength: PasswordStrength): string {
  if (strength === 'weak') return 'var(--error)';
  if (strength === 'medium') return 'var(--warning)';
  return 'var(--success)';
}

export function Register({ onSwitchToLogin, onSuccess }: RegisterProps) {
  const { register, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const passwordStrength = evaluatePasswordStrength(password);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError(null);
      clearError();

      if (password !== confirmPassword) {
        setLocalError('Passwords do not match');
        return;
      }

      if (passwordStrength === 'weak') {
        setLocalError('Please choose a stronger password');
        return;
      }

      try {
        await register(email, password, name);
        // After registration, go to connect provider screen
        onSuccess();
      } catch (err) {
        // Error is set in store
      }
    },
    [email, password, confirmPassword, name, passwordStrength, register, clearError, onSuccess]
  );

  const displayError = localError ?? error;

  return (
    <div
      className="flex flex-col items-center justify-center min-h-full px-4"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border)',
        }}
      >
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <h1
            className="text-2xl font-semibold tracking-tight mb-1"
            style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
          >
            Create your account
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Signa - AI-powered command center
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
            >
              Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
            >
              Master Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full px-3 py-2.5 pr-10 rounded-lg text-sm outline-none transition-colors"
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-primary)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: 'var(--text-tertiary)' }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  )}
              </button>
            </div>

            {/* Password strength indicator */}
            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1">
                  <div
                    className="h-1 flex-1 rounded-full transition-colors"
                    style={{ backgroundColor: password.length === 0 ? 'var(--bg-elevated)' : strengthColor(passwordStrength) }}
                  />
                  <span
                    className="text-xs font-mono"
                    style={{ color: strengthColor(passwordStrength) }}
                  >
                    {strengthLabel(passwordStrength)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-xs font-medium mb-1.5 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-colors"
              style={{
                backgroundColor: 'var(--bg-elevated)',
                border: confirmPassword && password !== confirmPassword ? '1px solid var(--error)' : '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = confirmPassword && password !== confirmPassword ? 'var(--error)' : 'var(--border)'; }}
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="mt-1 text-xs" style={{ color: 'var(--error)' }}>
                Passwords do not match
              </p>
            )}
          </div>

          {/* Security Warning */}
          <div
            className="p-3 rounded-lg text-xs leading-relaxed"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--warning) 10%, transparent)',
              border: '1px solid var(--warning)',
              color: 'var(--warning)',
            }}
          >
            <strong>This password encrypts your data.</strong> If lost, it cannot be recovered.
            There is no password reset - your data will be permanently inaccessible.
          </div>

          {/* Error */}
          {displayError && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--error) 10%, transparent)',
                border: '1px solid var(--error)',
                color: 'var(--error)',
              }}
            >
              {displayError}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            style={{
              backgroundColor: 'var(--accent)',
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--accent-strong)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)'; }}
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        {/* Provider note */}
        <div
          className="mt-5 p-3 rounded-lg text-xs leading-relaxed"
          style={{
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-secondary)',
          }}
        >
          Create your Signa account first. Gmail and Microsoft mailbox connections
          are added from the command center after sign-in.
        </div>

        {/* Footer */}
        <p className="text-center text-sm mt-6" style={{ color: 'var(--text-secondary)' }}>
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="font-medium underline underline-offset-2"
            style={{ color: 'var(--accent)' }}
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
